import React, { useState, useEffect, useRef, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import {
  getCashBookSetup,
  getCashBookBalances,
  getCashVouchers,
  createCashVoucher,
  voidJournalEntry,
} from "../../api/accountingApi";
import { useCurrency, formatMoney } from "../../common/utils/currency";

const toDateStr = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const emptyForm = { date: new Date(), amount: null, category_account_id: null, cash_account_id: null, description: "", reference: "" };

const CashBookPage = () => {
  const toast = useRef(null);
  const currency = useCurrency();
  const [setup, setSetup] = useState(null);
  const [balances, setBalances] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);
  const [dialogType, setDialogType] = useState(null); // 'in' | 'out' | null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [setupRes, balRes, listRes] = await Promise.all([
        getCashBookSetup(),
        getCashBookBalances(),
        getCashVouchers(typeFilter ? { type: typeFilter } : {}),
      ]);
      setSetup(setupRes);
      setBalances(balRes);
      setVouchers(listRes);
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: err.response?.data?.message || "Failed to load cash book" });
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const openDialog = (type) => {
    setForm({ ...emptyForm, date: new Date(), cash_account_id: setup?.defaultCashAccountId || null });
    setDialogType(type);
  };

  const handleSave = async () => {
    if (!form.amount || !form.category_account_id || !form.description?.trim()) {
      toast.current?.show({ severity: "warn", summary: "Missing fields", detail: "Amount, category, and description are required" });
      return;
    }
    setSaving(true);
    try {
      await createCashVoucher({
        type: dialogType,
        date: toDateStr(form.date),
        amount: form.amount,
        category_account_id: form.category_account_id,
        cash_account_id: form.cash_account_id,
        description: form.description.trim(),
        reference: form.reference?.trim() || null,
      });
      toast.current?.show({ severity: "success", summary: "Saved", detail: dialogType === "in" ? "Cash received recorded" : "Payment recorded" });
      setDialogType(null);
      await loadData();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: err.response?.data?.message || "Failed to save", life: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const handleVoid = async (row) => {
    const reason = window.prompt("Reason for cancelling this entry:");
    if (!reason) return;
    try {
      await voidJournalEntry(row.id, reason);
      toast.current?.show({ severity: "success", summary: "Cancelled", detail: `${row.entry_number} voided` });
      await loadData();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: err.response?.data?.message || "Failed to void" });
    }
  };

  // The non-cash line is the category (income/expense) side of the entry.
  const categoryOf = (row) => {
    const lines = row.lines || [];
    const catLine = row.entry_type === "cash_in"
      ? lines.find((l) => parseFloat(l.credit) > 0)
      : lines.find((l) => parseFloat(l.debit) > 0);
    return catLine?.account?.name || "—";
  };

  const amountOf = (row) => {
    const line = (row.lines || []).find((l) => parseFloat(l.debit) > 0);
    return parseFloat(line?.debit) || 0;
  };

  const typeTemplate = (row) =>
    row.entry_type === "cash_in"
      ? <Tag value="Cash In" severity="success" icon="pi pi-arrow-down-left" />
      : <Tag value="Cash Out" severity="danger" icon="pi pi-arrow-up-right" />;

  const statusTemplate = (row) =>
    row.status === "void" ? <Tag value="Cancelled" severity="secondary" /> : <Tag value="Posted" severity="info" />;

  const categoryOptions = dialogType === "in" ? setup?.inCategories : setup?.outCategories;

  return (
    <div className="p-3">
      <Toast ref={toast} />

      <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div>
          <h2 className="m-0">Cash Book</h2>
          <p className="text-500 mt-1 mb-0">Record money received and paid — accounting entries are created automatically</p>
        </div>
        <div className="flex gap-2">
          <Button label="Cash In" icon="pi pi-arrow-down-left" className="p-button-success" onClick={() => openDialog("in")} />
          <Button label="Cash Out" icon="pi pi-arrow-up-right" className="p-button-danger" onClick={() => openDialog("out")} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-3">
        {balances.map((b) => (
          <div key={b.id} className="surface-card border-round shadow-1 p-3 flex-1" style={{ minWidth: "14rem" }}>
            <div className="text-500 text-sm">{b.name}</div>
            <div className={`text-2xl font-bold mt-1 ${b.balance < 0 ? "text-red-500" : ""}`}>
              {formatMoney(b.balance, currency, { minimumFractionDigits: 2 })}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-2">
        <Dropdown
          value={typeFilter}
          options={[
            { label: "All", value: null },
            { label: "Cash In", value: "in" },
            { label: "Cash Out", value: "out" },
          ]}
          onChange={(e) => setTypeFilter(e.value)}
          placeholder="Filter by type"
          className="p-inputtext-sm"
        />
      </div>

      <DataTable value={vouchers} loading={loading} paginator rows={20} emptyMessage="No cash entries yet" size="small" stripedRows>
        <Column field="entry_number" header="Voucher #" style={{ minWidth: "8rem" }} />
        <Column field="date" header="Date" style={{ minWidth: "7rem" }} />
        <Column header="Type" body={typeTemplate} style={{ minWidth: "7rem" }} />
        <Column header="Category" body={categoryOf} style={{ minWidth: "10rem" }} />
        <Column field="description" header="Description" style={{ minWidth: "16rem" }} />
        <Column field="reference" header="Reference" style={{ minWidth: "7rem" }} />
        <Column header="Amount" body={(r) => formatMoney(amountOf(r), currency, { minimumFractionDigits: 2 })} style={{ minWidth: "8rem", textAlign: "right" }} />
        <Column header="Status" body={statusTemplate} style={{ minWidth: "6rem" }} />
        <Column
          body={(r) =>
            r.status === "posted" && (
              <Button icon="pi pi-times" className="p-button-text p-button-danger p-button-sm" tooltip="Cancel (void) this entry" onClick={() => handleVoid(r)} />
            )
          }
          style={{ width: "4rem" }}
        />
      </DataTable>

      <Dialog
        header={dialogType === "in" ? "Cash In — Money Received" : "Cash Out — Payment Made"}
        visible={!!dialogType}
        style={{ width: "min(30rem, 95vw)" }}
        modal
        onHide={() => setDialogType(null)}
        footer={
          <>
            <Button label="Cancel" className="p-button-text" onClick={() => setDialogType(null)} />
            <Button label="Save" icon="pi pi-check" loading={saving} onClick={handleSave} />
          </>
        }
      >
        <div className="flex flex-column gap-3 pt-2">
          <div className="flex flex-column gap-1">
            <label className="text-sm font-medium">Date</label>
            <Calendar value={form.date} onChange={(e) => setForm({ ...form, date: e.value })} dateFormat="yy-mm-dd" showIcon />
          </div>
          <div className="flex flex-column gap-1">
            <label className="text-sm font-medium">Amount *</label>
            <InputNumber value={form.amount} onValueChange={(e) => setForm({ ...form, amount: e.value })} minFractionDigits={0} maxFractionDigits={2} min={0} placeholder="0.00" />
          </div>
          <div className="flex flex-column gap-1">
            <label className="text-sm font-medium">{dialogType === "in" ? "Received for *" : "Paid for *"}</label>
            <Dropdown
              value={form.category_account_id}
              options={(categoryOptions || []).map((a) => ({ label: `${a.name} (${a.code})`, value: a.id }))}
              onChange={(e) => setForm({ ...form, category_account_id: e.value })}
              placeholder={dialogType === "in" ? "e.g. Fee Income, Other Income" : "e.g. Salaries, Rent, Utilities"}
              filter
            />
          </div>
          <div className="flex flex-column gap-1">
            <label className="text-sm font-medium">{dialogType === "in" ? "Deposit to" : "Pay from"}</label>
            <Dropdown
              value={form.cash_account_id}
              options={(setup?.cashAccounts || []).map((a) => ({ label: a.name, value: a.id }))}
              onChange={(e) => setForm({ ...form, cash_account_id: e.value })}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label className="text-sm font-medium">Description *</label>
            <InputTextarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} autoResize placeholder="What was this for?" />
          </div>
          <div className="flex flex-column gap-1">
            <label className="text-sm font-medium">Reference</label>
            <InputText value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Receipt / bill no. (optional)" />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default CashBookPage;
